import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Plus, Pencil, Trash2, Calendar } from "lucide-react";
import { getInspections, deleteInspection } from "../../../api/inspection";
import { getAgents } from "../../../api/agent";
import CreateOpenHomeModal from "../../inspections/components/CreateOpenHomeModal";
import { formatMelbourneDayLabel, formatMelbourneClock } from "../../../utils/melbourneTime";

/**
 * Open Homes tab on the property detail page (Monday 12731324906) — schedule,
 * edit and delete open-home times for this property, matching the reference
 * layout CG shared (Day / Start / End / Primary Agent / Secondary Agents +
 * an Add Open Home button).
 */
export default function OpenHomesTab({ propertyId, propertyTitle }) {
  const [openHomes, setOpenHomes] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingOpenHome, setEditingOpenHome] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInspections({ property: propertyId });
      const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
      list.sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
      setOpenHomes(list.filter((oh) => oh.status !== "cancelled"));
    } catch (err) {
      console.error(err);
      toast.error("Could not load open homes.");
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    if (!propertyId) return;
    load();
    getAgents({ page_size: 100 })
      .then((res) => setAgents(res?.data?.results || res?.data || []))
      .catch(() => setAgents([]));
  }, [propertyId, load]);

  const openAdd = () => { setEditingOpenHome(null); setModalOpen(true); };
  const openEdit = (oh) => { setEditingOpenHome(oh); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditingOpenHome(null); };

  const handleDelete = async (oh) => {
    if (!window.confirm(`Delete the open home on ${formatMelbourneDayLabel(oh.scheduled_date)}?`)) return;
    try {
      await deleteInspection(oh.id);
      toast.success("Open home deleted.");
      setOpenHomes((prev) => prev.filter((o) => o.id !== oh.id));
    } catch (err) {
      console.error(err);
      toast.error("Could not delete the open home.");
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-100">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-bold text-gray-800">Open Homes</h3>
        <button
          onClick={openAdd}
          className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-white bg-[#55C5D0] hover:bg-[#4AADB7] px-3 py-2 rounded-lg transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add Open Home
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-bold uppercase tracking-wide text-gray-400 border-b border-gray-100">
              <th className="px-5 py-3">Day</th>
              <th className="px-5 py-3">Start</th>
              <th className="px-5 py-3">End</th>
              <th className="px-5 py-3">Primary Agent</th>
              <th className="px-5 py-3">Secondary Agents</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-gray-400 text-xs">Loading…</td></tr>
            ) : openHomes.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-gray-400 text-xs">
                <Calendar className="w-6 h-6 mx-auto mb-2 text-gray-300" />
                No open home times defined
              </td></tr>
            ) : (
              openHomes.map((oh) => {
                // Always render in Melbourne time, not the viewer's browser
                // timezone, so the CRM agrees with REA / the buyer portal.
                const endInstant = new Date(new Date(oh.scheduled_date).getTime() + (oh.duration_minutes || 30) * 60000);
                const secondary = (oh.secondary_agents_detail || []).map((a) => a.name).join(", ");
                return (
                  <tr key={oh.id} className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50/50">
                    <td className="px-5 py-3 font-medium text-gray-800">{formatMelbourneDayLabel(oh.scheduled_date)}</td>
                    <td className="px-5 py-3 text-gray-600">{formatMelbourneClock(oh.scheduled_date)}</td>
                    <td className="px-5 py-3 text-gray-600">{formatMelbourneClock(endInstant)}</td>
                    <td className="px-5 py-3 text-gray-600">{oh.agent_name || "—"}</td>
                    <td className="px-5 py-3 text-gray-600">{secondary || "—"}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(oh)} className="text-gray-400 hover:text-[#55C5D0]" title="Edit">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(oh)} className="text-gray-400 hover:text-red-500" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <CreateOpenHomeModal
          propertyId={propertyId}
          propertyTitle={propertyTitle}
          agents={agents}
          inspection={editingOpenHome}
          onClose={closeModal}
          onCreated={() => { closeModal(); load(); }}
          onUpdated={() => { closeModal(); load(); }}
        />
      )}
    </div>
  );
}
